import { AxiosError } from 'axios';

import { isAxiosError } from 'src/utils/error';

import {
  type AzarRESTFailures,
  type AzarRPCExceptions,
  type RESTErrorCodes,
  type RPCErrorCodes,
  type RPCException,
} from './exception';

interface RPCMetadata {
  /** A String specifying the version of the JSON-RPC protocol. MUST be exactly "2.0". */
  jsonrpc: '2.0';
  /** An identifier established by the Client that MUST contain a String, Number, or NULL value if included. */
  id: number | string | null;
}

export interface RPCResponse<T> extends RPCMetadata {
  result: T;
}

export interface RPCErrorResponse<E> extends RPCMetadata {
  error: RPCException<E>;
}

export class RPCError<E> extends Error {
  code: RPCErrorCodes;

  data: E | undefined;

  constructor(data: RPCErrorResponse<E>) {
    super(data.error.message);

    this.code = data.error.code;

    /** TODO: fix typing */
    if (data) this.data = data.error.data as any;
  }
}

/**
 * JSON-RPC 메시지인지 확인합니다.
 * @param value
 * @returns
 */
export const isRPCMessage = (value: unknown): value is RPCMetadata => {
  return (
    value != null && typeof value === 'object' && 'jsonrpc' in value && value.jsonrpc === '2.0'
  );
};

/**
 * JSON-RPC 에서 에러가 발생했는지 확인합니다.
 * @param value 원본 응답
 * @returns
 */
export const isRPCErrorResponse = <E = unknown>(value: unknown): value is RPCErrorResponse<E> => {
  if (!isRPCMessage(value)) return false;

  return (
    'error' in value &&
    value.error != null &&
    typeof value.error === 'object' &&
    'code' in value.error
  );
};

/**
 * azar-api 에 정의되어 있는 exception 인지 확인합니다.
 */
export const isDefinedRPCException = <TCode extends RPCErrorCodes>(
  value: unknown,
  code: TCode
): value is Extract<AzarRPCExceptions, { code: TCode }> => {
  return value != null && typeof value === 'object' && 'code' in value && value.code === code;
};

/**
 * azar-api 에서 정의하고 있는 Exception 의 REST 버전인지 확인합니다.
 */
export const isDefinedAxiosRESTFailure = <TCode extends RESTErrorCodes>(
  value: unknown,
  code: TCode
): value is AxiosError<{ error: Extract<AzarRESTFailures, { code: TCode[1] }> }> => {
  if (!isAxiosError(value)) {
    return false;
  }

  const [status, codeText] = code;

  if (value.response?.status !== status) return false;

  /** TODO: 타입 가드 처리 개선 */
  const data = value.response.data as any;

  return [data?.error?.code, data?.code].some((c) => c === codeText);
};
